import { useEffect, useState } from 'react'

import { NotificationDetailModal } from 'features/notifications/components/notification-detail-modal/NotificationDetailModal'
import { NotificationsPanelView } from 'features/notifications/components/notifications-panel-view/NotificationsPanelView'
import { useListNotifications } from 'features/notifications/hooks/use-list-notifications'
import { useNotificationsService } from 'features/notifications/service'
import type { NotificationDto } from 'features/notifications/service/types'
import { useIsInternalUser } from 'hooks'

/**
 * NotificationsContainer — the internal-only gate in front of the bell.
 *
 * The surface is staff-only: the API answers 403 for any caller whose
 * verified email isn't `@meetyogi.com`. Deciding that here — from the
 * signed-in user's identity, the same check the backend runs — rather
 * than waiting for the request to come back 403 means external users
 * never mount the bell and never fire the list query: no flash, nothing
 * clickable, no repeating 403 poll. Fail-closed: not internal → no bell.
 *
 * Mounted globally by `routes/SignedInLayout` (overlay) AND inline by
 * layouts that prefer an in-bar bell (HomeView's Controls row, the
 * feature-flags Header).
 */
export const NotificationsContainer = (): JSX.Element => {
  if (!useIsInternalUser()) return <></>
  return <NotificationsBell />
}

/**
 * The bell-and-panel itself. Owns the open state for the dropdown, the
 * checkbox selection driving the header's bulk actions, and the
 * selected-notification state for the detail modal; wires the
 * `useListNotifications` hook to the pure `NotificationsPanelView`.
 *
 * Only one is on screen at a time; react-query dedupes the underlying
 * list query so the polling cost is shared.
 */
const NotificationsBell = (): JSX.Element => {
  const service = useNotificationsService()
  const [onlyUnread, setOnlyUnread] = useState(false)
  const {
    notifications,
    unreadCount,
    isLoading,
    isError,
    isForbidden,
    hasMore,
    isLoadingMore,
    loadMore,
    refetch,
    markManyAsRead,
    markManyAsUnread,
    deleteMany,
  } = useListNotifications(service, onlyUnread)

  const [open, setOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [selectedDetail, setSelectedDetail] = useState<NotificationDto | null>(
    null,
  )
  // Reveal latch: render nothing until the first list query settles
  // SUCCESSFULLY. Revealing on any non-403 settle let an early transient
  // error (a pre-auth blip, a 5xx) flip the latch on, then the real 403
  // settling ~7s later (default react-query retries) hid it again — the
  // appear-then-click-dead-then-vanish an external user saw. Gating on
  // success means the client never reveals the bell without the backend
  // having said yes, so a forbidden caller never sees it. Latched so later
  // refetches and the only-unread toggle (which swap the query key back
  // into a loading state) can't blink an already-revealed bell.
  const [revealed, setRevealed] = useState(false)
  useEffect(() => {
    if (!revealed && !isLoading && !isError) setRevealed(true)
  }, [revealed, isLoading, isError])

  const handleOpenChange = (nextOpen: boolean): void => {
    setOpen(nextOpen)
    // A stale selection resurfacing on the next open reads as a bug —
    // the checked rows may have scrolled away or been paged out.
    if (!nextOpen) setSelectedIds([])
  }

  const handleOnlyUnreadChange = (next: boolean): void => {
    setOnlyUnread(next)
    // The toggle swaps the visible list; checked rows may no longer be
    // rendered, so carrying the selection across would act on unseen rows.
    setSelectedIds([])
  }

  const handleToggleSelect = (id: number): void => {
    setSelectedIds((ids) =>
      ids.includes(id) ? ids.filter((v) => v !== id) : [...ids, id],
    )
  }

  const handleToggleSelectAll = (): void => {
    setSelectedIds((ids) =>
      ids.length === notifications.length ? [] : notifications.map((n) => n.id),
    )
  }

  const handleClearSelection = (): void => {
    setSelectedIds([])
  }

  const handleMarkSelectedRead = (): void => {
    void markManyAsRead(selectedIds)
    setSelectedIds([])
  }

  const handleMarkSelectedUnread = (): void => {
    void markManyAsUnread(selectedIds)
    setSelectedIds([])
  }

  const handleDeleteSelected = (): void => {
    void deleteMany(selectedIds)
    setSelectedIds([])
  }

  const handleRetry = (): void => {
    void refetch()
  }

  const handleMarkAsRead = (id: number): void => {
    void markManyAsRead([id])
  }

  const handleOpenDetail = (notification: NotificationDto): void => {
    setSelectedDetail(notification)
    // Close the dropdown so the modal isn't visually fighting it for
    // focus. The user can re-open the bell when the modal closes.
    setOpen(false)
  }

  const handleCloseDetail = (): void => {
    setSelectedDetail(null)
  }

  // Internal-only rollout: the API answers 403 for callers outside the
  // surface's audience — no bell at all beats a bell with a permanent
  // error panel. `!revealed` covers the settling window so the bell
  // never flashes for callers about to be forbidden.
  if (isForbidden || !revealed) return <></>

  return (
    <>
      <NotificationsPanelView
        notifications={notifications}
        unreadCount={unreadCount}
        isLoading={isLoading}
        isError={isError}
        onlyUnread={onlyUnread}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        open={open}
        selectedIds={selectedIds}
        onOpenChange={handleOpenChange}
        onOnlyUnreadChange={handleOnlyUnreadChange}
        onLoadMore={loadMore}
        onToggleSelect={handleToggleSelect}
        onToggleSelectAll={handleToggleSelectAll}
        onClearSelection={handleClearSelection}
        onMarkSelectedRead={handleMarkSelectedRead}
        onMarkSelectedUnread={handleMarkSelectedUnread}
        onDeleteSelected={handleDeleteSelected}
        onMarkAsRead={handleMarkAsRead}
        onRetry={handleRetry}
        onOpenDetail={handleOpenDetail}
      />
      <NotificationDetailModal
        notification={selectedDetail}
        onClose={handleCloseDetail}
      />
    </>
  )
}
